import { getAllTools, type Tool } from "./tools-data";

/**
 * Turns a free-text request like "merge my pdfs and then compress it and
 * then sign it" into an ordered list of real tool IDs, by splitting on
 * sequencing language and matching each resulting segment against the
 * actual tool catalog (name + description keyword overlap) -- not a
 * fixed, hardcoded set of example tools. Any combination of any tools on
 * the site can match; the merge/compress/sign example is just that, an
 * example of the PATTERN, not a special case this file knows about.
 *
 * This is honest, deterministic keyword matching, not an AI model -- it
 * will not understand phrasing it has no real word-overlap with, and it
 * intentionally requires each segment to produce a confident match
 * (rather than guessing at a low-confidence one) so a wrong 3-tool
 * pipeline is never silently offered in place of a right 2-tool one.
 */

export interface PipelineStepMatch {
  tool: Tool;
  /** The segment of the original query this step was matched from, kept
   * for display ("Matched from: 'compress it'") so the person can see
   * why this tool was picked, not just trust a black box. */
  matchedFrom: string;
}

const CONNECTOR_PATTERN = /\s*(?:,|;|\band then\b|\bthen\b|\bafter that\b|\bnext\b|\bfollowed by\b|\band finally\b|\bfinally\b|\band\b)\s*/gi;

const STOPWORDS = new Set([
  "a", "an", "the", "to", "it", "its", "my", "me", "please", "file", "files", "document", "documents",
  "i", "want", "need", "can", "you", "for", "of", "with", "this", "that", "then", "and", "also",
]);

/** Maps a word-form variant to the base form actually used in tool
 * names/descriptions. Plain plural-stripping (tokenize's own crude "s"
 * removal) doesn't catch "signature" -> "sign", "cropping" -> "crop",
 * etc. -- different words entirely, not just plurals. A real bug this
 * fixes: "add that to pdf AS SIGNATURE" scored zero name-word overlap
 * against "Sign PDF" because "signature" and "sign" are different
 * strings, so the segment lost to "Add PDF Password" on the strength
 * of "add"+"pdf" alone. Deliberately a small, explicit table (not
 * general stemming, which is more likely to introduce a wrong merge
 * than catch a real one) covering the verb-form variants this tool
 * catalog's own names and descriptions actually use. */
const SYNONYMS: Record<string, string> = {
  signature: "sign", signed: "sign", signing: "sign",
  cropping: "crop", cropped: "crop",
  resizing: "resize", resized: "resize",
  merging: "merge", merged: "merge",
  compressing: "compress", compressed: "compress",
  converting: "convert", converted: "convert",
  watermarking: "watermark", watermarked: "watermark",
  rotating: "rotate", rotated: "rotate",
  splitting: "split",
  extracting: "extract", extracted: "extract",
  protecting: "protect", protected: "protect",
  encrypting: "encrypt", encrypted: "encrypt",
  removing: "remove", removed: "remove",
};

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 1 && !STOPWORDS.has(w))
    .map((w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w)) // crude plural strip: "pdfs" -> "pdf", but "ss" endings ("access") left alone
    .map((w) => SYNONYMS[w] ?? w);
}

/** Splits a query into candidate step segments on sequencing language.
 * Segments with zero real (non-stopword) words are dropped -- no
 * signal at all to match a tool from. A single strong word ("compress
 * it", "sign it") is kept: that's genuinely how people phrase a
 * follow-up step referring back to the same file, and the confidence
 * threshold in matchSegmentToTool (below) is what actually guards
 * against a weak or wrong match, not segment length. */
function splitIntoSegments(query: string): string[] {
  return query
    .split(CONNECTOR_PATTERN)
    .map((s) => s.trim())
    .filter((s) => tokenize(s).length >= 1);
}

/** Extracts a "from X to Y" conversion direction from raw text (run
 * BEFORE stopword stripping, since "to" -- the one word that actually
 * carries the direction -- is itself a stopword and would otherwise be
 * lost). Tries a conversion-verb-anchored pattern first ("convert word
 * to pdf", "turn X into Y") -- necessary because a bare "X to Y" scan
 * on a sentence like "I WANT TO convert word TO pdf" matches "want to
 * convert" first (a real bug this fixes: that filler phrase isn't the
 * conversion direction, "word to pdf" is). Falls back to the bare
 * pattern only when no such verb is present. Both captured words are
 * singularized the same crude way tokenize() does, so "pdfs to word"
 * still matches a tool named "PDF to Word". */
function extractDirection(text: string): { from: string; to: string } | null {
  const lower = text.toLowerCase();
  const norm = (w: string) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
  const verbAnchored = lower.match(/\b(?:convert|turn|change|export|save)\s+([a-z0-9]+)\s+(?:to|into|as)\s+([a-z0-9]+)\b/);
  if (verbAnchored) return { from: norm(verbAnchored[1]), to: norm(verbAnchored[2]) };
  const bare = lower.match(/\b([a-z0-9]+)\s+(?:to|into)\s+([a-z0-9]+)\b/);
  if (bare) return { from: norm(bare[1]), to: norm(bare[2]) };
  return null;
}

/** Generic filler verbs that show up as a prefix across many unrelated
 * tool names ("Add Watermark", "Add PDF Password", "Add Page
 * Numbers"...) without actually identifying which one a segment
 * means -- unlike a word such as "compress" or "sign", which, even
 * though it also isn't unique to one tool, genuinely names the
 * action the person wants. Matching one of these against a tool's
 * name counts for much less than a real match. A real bug this
 * fixes: "add that to pdf as signature" scored "Add PDF Password"
 * higher than "Sign PDF" purely because "add" and "pdf" both
 * happened to be in that tool's name, even after the signature/sign
 * synonym fix closed most of the gap -- "add" needed to stop
 * counting as strong evidence on its own. Deliberately a small,
 * explicit list rather than a general word-frequency down-weight:
 * that was tried first and broke "compress it" -> Compress PDF (a
 * real, correct single-word match) purely because "compress" happens
 * to appear in a handful of tool names too, the same raw frequency as
 * "add" despite being a much stronger signal. */
const GENERIC_VERBS = new Set(["add", "get", "make", "create", "use", "apply", "do", "turn", "save"]);

function nameMatchWeight(word: string): number {
  return GENERIC_VERBS.has(word) ? 1 : 3;
}

/** Scores one segment against one tool: word-overlap count, weighted
 * higher for a hit in the tool's name (more precise signal) than in its
 * description (broader, noisier signal) -- and within name hits, full
 * weight unless the word is a generic filler verb (see
 * nameMatchWeight) rather than something that actually identifies the
 * tool. Directional converter names ("X to Y") are a further special
 * case -- see this function's own handling below for why plain word
 * overlap can't tell "PDF to Word" and "Word to PDF" apart on its own. */
function scoreSegmentAgainstTool(segmentWords: string[], segmentRaw: string, tool: Tool): number {
  const nameWords = new Set(tokenize(tool.name));
  const descWords = new Set(tokenize(tool.description));
  let score = 0;
  for (const w of segmentWords) {
    if (nameWords.has(w)) score += nameMatchWeight(w);
    else if (descWords.has(w)) score += 1;
  }

  // Directional check: "PDF to Word" and "Word to PDF" tokenize to the
  // identical word set ({pdf, word}) once "to" is stripped as a
  // stopword, so word-overlap scoring alone can't distinguish them --
  // this is the actual bug that surfaced live ("convert word to pdf"
  // matching PDF to Word instead of Word to PDF). When the segment and
  // the tool's name both express a direction, require them to agree;
  // an exactly-reversed direction disqualifies the tool outright
  // rather than just scoring it lower, since a reversed converter is
  // wrong, not just a worse guess.
  const segDir = extractDirection(segmentRaw);
  const toolDir = extractDirection(tool.name);
  if (segDir && toolDir) {
    const matches = segDir.from === toolDir.from && segDir.to === toolDir.to;
    const reversed = segDir.from === toolDir.to && segDir.to === toolDir.from;
    if (reversed) return 0;
    if (matches) score += 5;
  }

  return score;
}

const MIN_CONFIDENT_SCORE = 3; // roughly: at least one solid name-word hit, or several description hits

function matchSegmentToTool(segment: string, tools: Tool[]): Tool | null {
  const words = tokenize(segment);
  if (words.length === 0) return null;
  let best: Tool | null = null;
  let bestScore = 0;
  for (const tool of tools) {
    if (tool.hidden) continue;
    const score = scoreSegmentAgainstTool(words, segment, tool);
    if (score > bestScore) {
      bestScore = score;
      best = tool;
    }
  }
  return bestScore >= MIN_CONFIDENT_SCORE ? best : null;
}

/**
 * Returns an ordered pipeline of 2+ confidently-matched tools, or null
 * if the query doesn't look like a genuine multi-step request (fewer
 * than 2 segments produced a confident match). Never returns a partial
 * guess dressed up as a full answer -- a query that half-matches is
 * treated as no match, not a low-confidence one.
 */
export function parsePipelineRequest(query: string): PipelineStepMatch[] | null {
  const trimmed = query.trim();
  if (trimmed.length < 6) return null;

  const segments = splitIntoSegments(trimmed);
  if (segments.length < 2) return null;

  const tools = getAllTools();
  const matches: PipelineStepMatch[] = [];
  const usedToolIds = new Set<string>();

  for (const segment of segments) {
    const tool = matchSegmentToTool(segment, tools);
    if (tool && !usedToolIds.has(tool.id)) {
      matches.push({ tool, matchedFrom: segment });
      usedToolIds.add(tool.id);
    }
  }

  return matches.length >= 2 ? matches : null;
}
